/**
 * SupplementsSection — lista de suplementos: nombre / dosis / momento / notas.
 */
import { useEffect } from "react";
import { ensureIds, genId, type SupplementItem, type SupplementsData } from "../../../types/plan";
import { Icon } from "../../../lib/Icon";
import { InlineEditable } from "../InlineEditable";
import { PageHeader, SectionPage } from "./section-shared";

interface SupplementsSectionProps {
  data: SupplementsData;
  onChange: (next: SupplementsData) => void;
  readonly?: boolean;
}

export function SupplementsSection({ data, onChange, readonly }: SupplementsSectionProps) {
  // Garantizar IDs estables (necesario para fieldPath persistente al reordenar).
  useEffect(() => {
    if (readonly) return;
    const withIds = ensureIds(data.items || [], "sp");
    if (withIds !== data.items) {
      onChange({ ...data, items: withIds });
    }
  }, [data, readonly, onChange]);

  const updateItem = (i: number, patch: Partial<SupplementItem>) => {
    const items = [...data.items];
    items[i] = { ...items[i], ...patch };
    onChange({ ...data, items });
  };

  const removeItem = (i: number) => {
    const items = [...data.items];
    items.splice(i, 1);
    onChange({ ...data, items });
  };

  const addItem = () => {
    onChange({
      ...data,
      items: [...data.items, { id: genId("sp"), name: "Nuevo suplemento", dose: "", timing: "", notes: "" }],
    });
  };

  return (
    <SectionPage readonly={readonly}>
      <PageHeader eyebrow="Suplementación" title="Suplementación" />
      <p className="al-pg-summary">
        <InlineEditable
          value={data.summary || ""}
          onChange={(v) => onChange({ ...data, summary: v })}
          multiline
          readonly={readonly}
          placeholder="Resumen…"
          fieldPath="summary"
        />
      </p>
      <table className="al-pg-table al-pg-table-full">
        <thead>
          <tr>
            <th>Suplemento</th>
            <th>Dosis</th>
            <th>Momento</th>
            <th>Notas</th>
            {!readonly && <th></th>}
          </tr>
        </thead>
        <tbody>
          {data.items.map((it, i) => {
            const iid = it.id || `idx${i}`;
            return (
            <tr key={it.id || i}>
              <td>
                <b>
                  <InlineEditable value={it.name} onChange={(v) => updateItem(i, { name: v })} readonly={readonly} fieldPath={`itm.${iid}.name`} />
                </b>
              </td>
              <td>
                <InlineEditable value={it.dose} onChange={(v) => updateItem(i, { dose: v })} readonly={readonly} fieldPath={`itm.${iid}.dose`} />
              </td>
              <td>
                <InlineEditable value={it.timing} onChange={(v) => updateItem(i, { timing: v })} readonly={readonly} fieldPath={`itm.${iid}.timing`} />
              </td>
              <td>
                <InlineEditable value={it.notes} onChange={(v) => updateItem(i, { notes: v })} readonly={readonly} fieldPath={`itm.${iid}.notes`} />
              </td>
              {!readonly && (
                <td>
                  <button type="button" className="al-ex-del-btn" onClick={() => removeItem(i)} title="Eliminar">
                    <Icon name="x" size={12} />
                  </button>
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>
      {!readonly && (
        <button type="button" className="al-day-add-ex" onClick={addItem} style={{ marginTop: 8 }}>
          <Icon name="plus" size={12} /> Agregar suplemento
        </button>
      )}
    </SectionPage>
  );
}
